import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Grid,
  Paper,
  LinearProgress,
  Avatar,
  Badge,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
} from '@mui/material';
import {
  Send,
  Add,
  Delete,
  Visibility,
  CheckCircle,
  People,
  TrendingUp,
  Notifications as NotificationsIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  Close,
  FlashOn,
  NotificationsActive,
  LockOutlined,
  BarChart,
  ArrowForward,
  CalendarToday,
  Person,
  TouchApp,
  ShoppingCartOutlined,
  ArrowForwardIos,
  WhatsApp,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useMyLocations } from '../hooks/useMyLocations';
import BranchSelect from '../components/BranchSelect';
import SegmentSelector, { SEGMENTS, STUDIO_SEGMENTS } from '../components/notifications/SegmentSelector';
import NotificationPreview from '../components/notifications/NotificationPreview';
import TemplateSelector from '../components/notifications/TemplateSelector';
import WhatsAppHub, { canUseWhatsApp, isWhatsAppLocked, WhatsAppLocked } from '../components/whatsapp/WhatsAppHub';
import FeatureLocked, { isPushLocked } from '../components/FeatureLocked';
import { useSearchParams } from 'react-router-dom';

// ─── Types ───────────────────────────────────
interface Campaign {
  _id: string;
  title: string;
  notificationTitle: string;
  notificationBody: string;
  imageUrl?: string;
  deepLinkType: string;
  deepLinkId?: string;
  segment: string;
  segmentConfig: any;
  status: 'SENT' | 'FAILED' | 'DRAFT' | 'SCHEDULED';
  sentAt?: string;
  scheduledAt?: string;
  targetedCount: number;
  successCount: number;
  failureCount: number;
  openedCount: number;
  failureReasons?: string[];
  createdBy: string;
  createdAt: string;
  /** Set for a studio-owned campaign (null = SweatSocial global). */
  studioId?: string | null;
}

interface Lead {
  id: string;
  bookingId: string;
  bookingType: string;
  isFromWebsite: boolean;
  user: {
    id: string;
    name: string;
    email: string;
    phoneNumber: string;
    city: string;
    image: string;
    hasToken: boolean;
  };
  sessionId?: string;
  sessionDate?: string;
  sessionClassName?: string;
  sessionDuration?: number;
  sessionPrice?: number;
  eventId?: string;
  eventName?: string;
  eventDate?: string;
  eventPrice?: number;
  studioId?: string;
  studioName?: string;
  amount?: number;
  abandonedAt: string;
}

interface CampaignForm {
  title: string;
  notificationTitle: string;
  notificationBody: string;
  imageUrl: string;
  deepLinkType: string;
  deepLinkId: string;
  deepLinkUrl: string;
  segment: string;
  segmentConfig: any;
  scheduledAt: string; // ISO string or '' for immediate
  _leadUserName?: string; // display hint only, not sent to API
}

const EMPTY_FORM: CampaignForm = {
  title: '',
  notificationTitle: '',
  notificationBody: '',
  imageUrl: '',
  deepLinkType: 'NONE',
  deepLinkId: '',
  deepLinkUrl: '',
  segment: 'ALL_USERS',
  segmentConfig: {},
  scheduledAt: '',
};

const timeAgo = (dateStr: string) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

// ─── Date helpers ─────────────────────────────────────────────────────────────
//
// Session/Event dates are stored as "fake UTC" — IST wall-clock time stored as UTC.
// (e.g. "18:30 UTC" really means "18:30 IST"). Read UTC components directly.
//
// scheduledAt is stored as REAL UTC (with +05:30 offset sent from the browser).
// Use toLocaleString(Asia/Kolkata) for those.
// ──────────────────────────────────────────────────────────────────────────────

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fakeUtcToISTTime = (date: Date): string => {
  let h = date.getUTCHours();
  const m = String(date.getUTCMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${String(h).padStart(2, '0')}:${m} ${ampm}`;
};

const fakeUtcToISTDate = (date: Date): string => {
  return `${WEEKDAYS[date.getUTCDay()]}, ${String(date.getUTCDate()).padStart(2, '0')} ${MONTHS[date.getUTCMonth()]}`;
};

const formatISTDate = (dateStr?: string) => {
  if (!dateStr) return null;
  return fakeUtcToISTDate(new Date(dateStr));
};

const formatISTTime = (dateStr?: string) => {
  if (!dateStr) return null;
  return fakeUtcToISTTime(new Date(dateStr));
};

// For scheduledAt from campaign API response (stored as REAL UTC, needs IST timezone conversion)
const formatScheduledAt = (dateStr?: string): string | null => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const weekday = d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short' });
  const day = d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit' });
  const month = d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short' });
  const time = d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true });
  return `${weekday}, ${day} ${month} · ${time} IST`;
};

// For displaying the datetime-local input value (e.g. "2026-03-09T10:30") in human format.
// Do NOT parse through Date — read digits directly to avoid browser timezone interference.
const formatLocalScheduleTime = (localStr?: string): string | null => {
  if (!localStr) return null;
  const timePart = localStr.slice(11, 16); // "10:30"
  const [hStr, mStr] = timePart.split(':');
  let h = parseInt(hStr, 10);
  const m = mStr;
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${String(h).padStart(2, '0')}:${m} ${ampm}`;
};

const formatLocalSchedule = (localStr?: string): string | null => {
  if (!localStr) return null;
  // date part: "2026-03-09" → parse as UTC to avoid DST shifts
  const datePart = localStr.slice(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(Date.UTC(year, month - 1, day));
  return `${WEEKDAYS[d.getUTCDay()]}, ${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]} · ${formatLocalScheduleTime(localStr)} IST`;
};

// Returns current IST time as a naive datetime-local string ("YYYY-MM-DDTHH:mm")
const nowISTLocal = (): string => {
  return new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 16);
};

// How far in future — compare fake-UTC date against current real time shifted to fake-UTC offset
const timeUntil = (dateStr?: string) => {
  if (!dateStr) return null;
  const nowFakeUTC = Date.now() + 5.5 * 3600000;
  const diff = new Date(dateStr).getTime() - nowFakeUTC;
  if (diff <= 0) return null;
  const hrs = Math.floor(diff / 3600000);
  if (hrs < 24) return `in ${hrs}h`;
  return `in ${Math.floor(hrs / 24)}d`;
};

// ─── Stats Card ───────────────────────────────
const StatCard: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
  gradient: string;
  iconBg: string;
}> = ({ label, value, icon, gradient, iconBg }) => (
  <Box
    sx={{
      background: gradient,
      borderRadius: 3,
      p: 2.5,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      position: 'relative',
      overflow: 'hidden',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: -20,
        right: -20,
        width: 100,
        height: 100,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.07)',
      },
    }}
  >
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: 2.5,
        bgcolor: iconBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        backdropFilter: 'blur(4px)',
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 500, mt: 0.3, display: 'block' }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

// ─── Campaign Row Card ────────────────────────
const CampaignCard: React.FC<{
  campaign: Campaign;
  onView: () => void;
  onDelete: () => void;
  onCancelSchedule?: () => void;
}> = ({ campaign, onView, onDelete, onCancelSchedule }) => {
  const isSent = campaign.status === 'SENT';
  const isScheduled = campaign.status === 'SCHEDULED';
  const deliveryRate = campaign.targetedCount > 0
    ? Math.round((campaign.successCount / campaign.targetedCount) * 100)
    : 0;
  const openRate = campaign.successCount > 0
    ? Math.round(((campaign.openedCount || 0) / campaign.successCount) * 100)
    : 0;

  const segmentLabel = SEGMENTS.find((s) => s.value === campaign.segment)?.label || campaign.segment;
  const segmentIcon = SEGMENTS.find((s) => s.value === campaign.segment)?.icon || '📢';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderRadius: 2.5,
        bgcolor: '#fff',
        border: '1px solid rgba(0,0,0,0.06)',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'rgba(33,150,243,0.3)',
          boxShadow: '0 4px 20px rgba(33,150,243,0.08)',
        },
      }}
    >
      {/* Icon */}
      <Avatar
        sx={{
          width: 44,
          height: 44,
          bgcolor: isSent
            ? 'rgba(76,175,80,0.1)'
            : isScheduled
            ? 'rgba(255,152,0,0.1)'
            : 'rgba(244,67,54,0.1)',
          fontSize: '1.2rem',
          flexShrink: 0,
        }}
      >
        {isSent ? '✅' : isScheduled ? '⏰' : '❌'}
      </Avatar>

      {/* Main content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {campaign.title}
          </Typography>
          <Chip
            label={isSent ? 'Sent' : isScheduled ? 'Scheduled' : 'Failed'}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 700,
              bgcolor: isSent
                ? 'rgba(76,175,80,0.12)'
                : isScheduled
                ? 'rgba(255,152,0,0.15)'
                : 'rgba(244,67,54,0.12)',
              color: isSent ? '#2e7d32' : isScheduled ? '#e65100' : '#c62828',
              flexShrink: 0,
            }}
          />
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ display: 'block', mb: 0.5 }}
        >
          {campaign.notificationTitle}
        </Typography>

        {/* Stats row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Typography sx={{ fontSize: '0.7rem' }}>{segmentIcon}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {segmentLabel}
            </Typography>
          </Box>
          <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <People sx={{ fontSize: 11, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">
              {campaign.targetedCount} targeted
            </Typography>
          </Box>
          {(campaign.openedCount || 0) > 0 && (
            <>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TouchApp sx={{ fontSize: 11, color: '#9c27b0' }} />
                <Typography variant="caption" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                  {campaign.openedCount} opened ({openRate}%)
                </Typography>
              </Box>
            </>
          )}
          <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: 11, color: isScheduled ? '#e65100' : 'text.disabled' }} />
            <Typography variant="caption" color={isScheduled ? '#e65100' : 'text.secondary'} fontWeight={isScheduled ? 600 : 400}>
              {isScheduled && campaign.scheduledAt
                ? `Fires ${formatScheduledAt(campaign.scheduledAt)}`
                : campaign.sentAt
                ? new Date(campaign.sentAt).toLocaleString('en-IN', {
                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                  })
                : '—'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Delivery rate */}
      <Box sx={{ textAlign: 'center', minWidth: 68, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{ color: deliveryRate >= 80 ? '#2e7d32' : deliveryRate > 0 ? '#e65100' : '#c62828', lineHeight: 1 }}
        >
          {deliveryRate}%
        </Typography>
        <Typography variant="caption" color="text.secondary">delivered</Typography>
        <LinearProgress
          variant="determinate"
          value={deliveryRate}
          sx={{
            mt: 0.5,
            height: 4,
            borderRadius: 4,
            bgcolor: 'rgba(0,0,0,0.06)',
            '& .MuiLinearProgress-bar': {
              bgcolor: deliveryRate >= 80 ? '#4caf50' : deliveryRate > 0 ? '#ff9800' : '#f44336',
            },
          }}
        />
      </Box>

      {/* Stats pills */}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, flexShrink: 0 }}>
        <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: 'rgba(76,175,80,0.08)', borderRadius: 1.5 }}>
          <Typography variant="caption" fontWeight={700} color="success.dark" display="block">
            {campaign.successCount}
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>ok</Typography>
        </Box>
        {(campaign.openedCount || 0) > 0 && (
          <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: 'rgba(156,39,176,0.08)', borderRadius: 1.5 }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: '#6a1b9a' }} display="block">
              {campaign.openedCount}
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>opened</Typography>
          </Box>
        )}
        {campaign.failureCount > 0 && (
          <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: 'rgba(244,67,54,0.08)', borderRadius: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="error.dark" display="block">
              {campaign.failureCount}
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>fail</Typography>
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        <Tooltip title="View Details">
          <IconButton size="small" onClick={onView} sx={{ color: 'text.secondary', '&:hover': { color: '#2196f3', bgcolor: 'rgba(33,150,243,0.08)' } }}>
            <Visibility sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        {isScheduled && onCancelSchedule && (
          <Tooltip title="Cancel Schedule">
            <IconButton size="small" onClick={onCancelSchedule} sx={{ color: '#e65100', '&:hover': { bgcolor: 'rgba(255,152,0,0.1)' } }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete">
          <IconButton size="small" onClick={onDelete} sx={{ color: 'text.secondary', '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.08)' } }}>
            <Delete sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

// ─── Campaign Detail Dialog ───────────────────
const CampaignDetailDialog: React.FC<{ campaign: Campaign | null; onClose: () => void }> = ({ campaign, onClose }) => {
  if (!campaign) return null;
  const deliveryRate = campaign.targetedCount > 0
    ? Math.round((campaign.successCount / campaign.targetedCount) * 100) : 0;
  const openRate = campaign.successCount > 0
    ? Math.round(((campaign.openedCount || 0) / campaign.successCount) * 100) : 0;
  const isSent = campaign.status === 'SENT';

  return (
    <Dialog open={!!campaign} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ background: isSent ? 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)' : 'linear-gradient(135deg, #b71c1c 0%, #c62828 100%)', p: 3, position: 'relative' }}>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
          <Close />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', width: 44, height: 44 }}>
            <NotificationsActive />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>{campaign.title}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {campaign.sentAt ? new Date(campaign.sentAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'Not sent'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Notification preview */}
        <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 2, border: '1px solid rgba(0,0,0,0.06)', mb: 2.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Notification
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>{campaign.notificationTitle}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>{campaign.notificationBody}</Typography>
          {campaign.deepLinkType && campaign.deepLinkType !== 'NONE' && campaign.deepLinkId && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LinkIcon sx={{ fontSize: 13, color: '#2196f3' }} />
              <Typography variant="caption" sx={{ color: '#2196f3' }}>
                {campaign.deepLinkType}: {campaign.deepLinkId}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Stats */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          {[
            { label: 'Targeted', value: campaign.targetedCount, color: '#1565c0', bg: 'rgba(21,101,192,0.08)' },
            { label: 'Delivered', value: campaign.successCount, color: '#2e7d32', bg: 'rgba(46,125,50,0.08)' },
            { label: 'Opened', value: campaign.openedCount || 0, color: '#6a1b9a', bg: 'rgba(106,27,154,0.08)', sub: openRate > 0 ? `${openRate}% open rate` : undefined },
            { label: 'Failed', value: campaign.failureCount, color: '#c62828', bg: 'rgba(198,40,40,0.08)' },
          ].map((s) => (
            <Grid size={{ xs: 6, sm: 3 }} key={s.label}>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: s.bg, borderRadius: 2 }}>
                <Typography variant="h5" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                {s.sub && <Typography sx={{ fontSize: '0.6rem', color: s.color, display: 'block' }}>{s.sub}</Typography>}
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Delivery bar */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Delivery Rate</Typography>
            <Typography variant="caption" fontWeight={800} sx={{ color: deliveryRate >= 80 ? '#2e7d32' : '#e65100' }}>
              {deliveryRate}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={deliveryRate}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(0,0,0,0.06)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: deliveryRate >= 80
                  ? 'linear-gradient(90deg, #4caf50, #2e7d32)'
                  : 'linear-gradient(90deg, #ff9800, #e65100)',
              },
            }}
          />
        </Box>

        {(campaign.openedCount || 0) > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Open Rate</Typography>
              <Typography variant="caption" fontWeight={800} sx={{ color: '#6a1b9a' }}>
                {openRate}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={openRate}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(0,0,0,0.06)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #9c27b0, #6a1b9a)',
                },
              }}
            />
          </Box>
        )}

        {/* Failure reasons */}
        {campaign.failureCount > 0 && campaign.failureReasons && campaign.failureReasons.length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(198,40,40,0.05)', borderRadius: 2, border: '1px solid rgba(198,40,40,0.2)' }}>
            <Typography variant="caption" fontWeight={700} color="error" sx={{ display: 'block', mb: 0.75 }}>
              FCM Error{campaign.failureReasons.length > 1 ? 's' : ''}:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {campaign.failureReasons.map((reason) => (
                <Chip
                  key={reason}
                  label={reason.replace('messaging/', '')}
                  size="small"
                  sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(198,40,40,0.1)', color: '#c62828', fontFamily: 'monospace' }}
                />
              ))}
            </Box>
            {campaign.failureReasons.includes('messaging/registration-token-not-registered') && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Stale tokens have been automatically removed from user records.
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Box>
              <Typography variant="caption" color="text.secondary">Sent by</Typography>
              <Typography variant="body2" fontWeight={600}>{campaign.createdBy}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.85rem' }}>
              {SEGMENTS.find((s) => s.value === campaign.segment)?.icon || '📢'}
            </Typography>
            <Box>
              <Typography variant="caption" color="text.secondary">Audience</Typography>
              <Typography variant="body2" fontWeight={600}>
                {SEGMENTS.find((s) => s.value === campaign.segment)?.label || campaign.segment}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// ─── Create Campaign Form ─────────────────────
interface CreateFormProps {
  onSuccess: (campaign: Campaign) => void;
  /** Re-pull (and briefly poll) the campaign list so the card shows the settled
   *  status/counts instead of the create-response snapshot. */
  onRefresh?: () => void;
  adminEmail: string;
  initialForm?: Partial<CampaignForm>;
  initialStep?: number;
  onFormConsumed?: () => void;
  isStudio?: boolean; // studio owner mode — uses studio-scoped endpoints + limited segments
}

const CreateCampaignForm: React.FC<CreateFormProps> = ({ onSuccess, onRefresh, adminEmail, initialForm, initialStep, onFormConsumed, isStudio }) => {
  const studioDefaultForm = isStudio ? { ...EMPTY_FORM, segment: 'ALL_STUDIO_USERS' } : EMPTY_FORM;
  // Under "All locations" the audience and the send have to name one
  // location — the switcher's __all__ is refused for writes (and the audience
  // preview is a POST). Single-location owners never see this.
  const { isAllLocations } = useMyLocations();
  const [campaignBranch, setCampaignBranch] = useState('');
  const mustPickBranch = !!isStudio && isAllLocations;
  const pinStudioId = mustPickBranch ? campaignBranch || undefined : undefined;
  const [form, setForm] = useState<CampaignForm>({ ...studioDefaultForm, ...initialForm });
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [loading, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Not an error — the send is still running server-side (client-side timeout).
  const [notice, setNotice] = useState<string | null>(null);
  const [step, setStep] = useState(initialStep ?? 0);

  // Apply injected form/step if parent updates them (lead pre-fill)
  useEffect(() => {
    if (initialForm && Object.keys(initialForm).length > 0) {
      setForm({ ...EMPTY_FORM, ...initialForm });
      setStep(initialStep ?? 1);
      onFormConsumed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialForm]);

  const handleTemplateSelect = (template: any) => {
    setSelectedTemplate(template.key);
    setForm((prev) => ({
      ...prev,
      notificationTitle: template.title,
      notificationBody: template.body,
      segment: template.segment,
      segmentConfig: template.segmentConfig || {},
      title: prev.title || template.label,
    }));
    setStep(1);
  };

  const handleSend = async () => {
    if (!form.title.trim()) { setError('Campaign title is required'); return; }
    if (!form.notificationTitle.trim()) { setError('Notification title is required'); return; }
    if (!form.notificationBody.trim()) { setError('Notification body is required'); return; }
    if (mustPickBranch && !campaignBranch) { setError('Choose which location this campaign is for'); return; }
    try {
      setSending(true);
      setError(null);
      setNotice(null);
      const { _leadUserName, ...payload } = form;
      // Append IST offset (+05:30) so the server stores the correct real-UTC timestamp
      const scheduledAt = payload.scheduledAt
        ? `${payload.scheduledAt}:00+05:30`
        : undefined;
      const campaignData = { ...payload, scheduledAt, createdBy: adminEmail };
      const result: any = isStudio
        ? await apiService.createStudioNotificationCampaign(campaignData, pinStudioId)
        : await apiService.createNotificationCampaign(campaignData);
      onSuccess(result.campaign);
      // A scheduled campaign won't change until its fire time — only a
      // send-now campaign needs the settle poll.
      if (!scheduledAt) onRefresh?.();
      setForm(EMPTY_FORM);
      setSelectedTemplate(null);
      setStep(0);
    } catch (err: any) {
      // status 0 = the request went out but no response came back (client-side
      // timeout / dropped connection). The campaign row is created before the
      // FCM fan-out starts and the send completes on its own, so this is "still
      // working", not "failed" — don't show it as an error, and pull the list so
      // the real result appears once it settles.
      const stillSending = err?.status === 0 || /timeout|network/i.test(err?.message || '');
      if (stillSending) {
        setNotice(
          'Still sending — a large audience can take a minute. This campaign was created; open the Campaigns tab shortly to see the delivery result.',
        );
        onRefresh?.();
      } else {
        setError(err.message || 'Failed to send campaign');
      }
    } finally {
      setSending(false);
    }
  };

  const steps = [
    { label: 'Audience', icon: '👥' },
    { label: 'Message', icon: '✍️' },
    { label: 'Send', icon: '🚀' },
  ];

  return (
    <Box>
      {/* Step indicator */}
      <Box sx={{ display: 'flex', gap: 0, mb: 4, position: 'relative' }}>
        {steps.map((s, i) => (
          <Box key={s.label} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', cursor: i < step ? 'pointer' : 'default' }} onClick={() => i < step && setStep(i)}>
            {/* connector line */}
            {i < steps.length - 1 && (
              <Box sx={{ position: 'absolute', top: 20, left: '50%', right: '-50%', height: 2, bgcolor: step > i ? '#2196f3' : 'rgba(0,0,0,0.1)', zIndex: 0, transition: 'background-color 0.3s' }} />
            )}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                fontSize: step === i ? '1.2rem' : '1rem',
                bgcolor: step === i ? '#2196f3' : step > i ? '#e3f2fd' : '#f5f5f5',
                border: step === i ? '2px solid #1976d2' : step > i ? '2px solid #2196f3' : '2px solid #e0e0e0',
                transition: 'all 0.3s',
                boxShadow: step === i ? '0 0 0 4px rgba(33,150,243,0.15)' : 'none',
              }}
            >
              {step > i ? <CheckCircle sx={{ fontSize: 18, color: '#2196f3' }} /> : <Typography sx={{ fontSize: step === i ? '1.1rem' : '0.9rem' }}>{s.icon}</Typography>}
            </Box>
            <Typography variant="caption" fontWeight={step === i ? 700 : 400} sx={{ mt: 0.5, color: step === i ? '#2196f3' : 'text.secondary' }}>
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Step 0: Audience */}
      {step === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ width: 4, height: 20, bgcolor: '#2196f3', borderRadius: 2 }} />
              <Typography variant="subtitle1" fontWeight={700}>Quick Start — Pick a Template</Typography>
            </Box>
            {!isStudio && <TemplateSelector selectedKey={selectedTemplate} onSelect={handleTemplateSelect} />}
            {isStudio && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1, fontStyle: 'italic' }}>
                Fill in the details below to compose your notification.
              </Typography>
            )}
          </Box>

          <Divider><Chip label="or configure manually" size="small" sx={{ fontSize: '0.7rem', bgcolor: 'white' }} /></Divider>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ width: 4, height: 20, bgcolor: '#2196f3', borderRadius: 2 }} />
              <Typography variant="subtitle1" fontWeight={700}>Select Audience</Typography>
            </Box>
            {mustPickBranch && (
              <Box sx={{ mb: 2, maxWidth: 360 }}>
                <BranchSelect
                  value={campaignBranch}
                  onChange={(v) => {
                    setCampaignBranch(v);
                    // The audience filters (classes, packs, members…) were
                    // picked from the previous location's lists.
                    setForm((p) => ({ ...p, segmentConfig: {} }));
                  }}
                  minRole="manager"
                />
              </Box>
            )}
            {mustPickBranch && !campaignBranch ? (
              <Typography variant="body2" color="text.secondary">
                Choose a location to see its audience.
              </Typography>
            ) : (
              <SegmentSelector
                key={pinStudioId || 'default'}
                segment={form.segment}
                segmentConfig={form.segmentConfig}
                onSegmentChange={(s) => setForm((p) => ({ ...p, segment: s, segmentConfig: {} }))}
                onConfigChange={(c) => setForm((p) => ({ ...p, segmentConfig: c }))}
                allowedSegments={isStudio ? STUDIO_SEGMENTS : undefined}
                previewFn={isStudio ? (seg, cfg, all) => apiService.getStudioSegmentPreview(seg, cfg, all, pinStudioId) : undefined}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" endIcon={<ArrowForward />} onClick={() => setStep(1)} sx={{ borderRadius: 2, px: 3 }}>
              Next: Compose
            </Button>
          </Box>
        </Box>
      )}

      {/* Step 1: Message */}
      {step === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Lead targeting note */}
          {form._leadUserName && (
            <Box sx={{ p: 1.5, bgcolor: 'rgba(156,39,176,0.06)', border: '1px solid rgba(156,39,176,0.2)', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person sx={{ fontSize: 16, color: '#9c27b0' }} />
              <Typography variant="caption" sx={{ color: '#6a1b9a', fontWeight: 600 }}>
                Targeting lead: {form._leadUserName}
              </Typography>
            </Box>
          )}

          <TextField
            label="Campaign Name"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            placeholder="e.g. March Re-engagement Blast"
            fullWidth
            helperText="Internal label — not shown to users"
          />

          <TextField
            label="Notification Title"
            value={form.notificationTitle}
            onChange={(e) => setForm((p) => ({ ...p, notificationTitle: e.target.value }))}
            placeholder="e.g. Don't miss out! 🏃"
            fullWidth
            required
            helperText={`${form.notificationTitle.length}/65 · Use {{name}} to personalise`}
            inputProps={{ maxLength: 65 }}
            sx={{ '& .MuiFormHelperText-root': { color: form.notificationTitle.length > 55 ? 'warning.main' : undefined } }}
          />

          <TextField
            label="Notification Body"
            value={form.notificationBody}
            onChange={(e) => setForm((p) => ({ ...p, notificationBody: e.target.value }))}
            placeholder="Hey {{name}}, your class is today. Get ready! 💪"
            fullWidth
            required
            multiline
            rows={3}
            helperText={`${form.notificationBody.length}/178 · Use {{name}} for name, {{city}} for city`}
            inputProps={{ maxLength: 178 }}
            sx={{ '& .MuiFormHelperText-root': { color: form.notificationBody.length > 160 ? 'warning.main' : undefined } }}
          />

          <TextField
            label="Image URL (optional)"
            value={form.imageUrl}
            onChange={(e) => setForm((p) => ({ ...p, imageUrl: e.target.value }))}
            placeholder="https://example.com/banner.jpg"
            fullWidth
            InputProps={{ startAdornment: <ImageIcon sx={{ mr: 1, color: 'text.disabled', fontSize: 20 }} /> }}
          />

          {/* Deep Link section */}
          <Box sx={{ p: 2, bgcolor: 'rgba(33,150,243,0.04)', borderRadius: 2, border: '1px solid rgba(33,150,243,0.12)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <LinkIcon sx={{ fontSize: 16, color: '#2196f3' }} />
              <Typography variant="caption" fontWeight={700} color="primary">Deep Link — Where to take the user on tap</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel>Link To</InputLabel>
                <Select value={form.deepLinkType} label="Link To" onChange={(e) => setForm((p) => ({ ...p, deepLinkType: e.target.value, deepLinkId: '', deepLinkUrl: '' }))}>
                  <MenuItem value="NONE">No Deep Link</MenuItem>
                  <MenuItem value="STUDIO">🏋️ Studio Page</MenuItem>
                  <MenuItem value="SESSION">💪 Session Page</MenuItem>
                  <MenuItem value="EVENT">🎪 Event Page</MenuItem>
                  <MenuItem value="LINK">🔗 Custom URL</MenuItem>
                </Select>
              </FormControl>

              {(form.deepLinkType === 'STUDIO' || form.deepLinkType === 'SESSION' || form.deepLinkType === 'EVENT') && (
                <TextField
                  label={`${form.deepLinkType} ID`}
                  value={form.deepLinkId}
                  onChange={(e) => setForm((p) => ({ ...p, deepLinkId: e.target.value }))}
                  placeholder="MongoDB ObjectId"
                  sx={{ flex: 1 }}
                  helperText="Paste the ID from the database"
                  InputProps={{ startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.disabled', fontSize: 20 }} /> }}
                />
              )}

              {form.deepLinkType === 'LINK' && (
                <TextField
                  label="Custom URL"
                  value={form.deepLinkUrl}
                  onChange={(e) => setForm((p) => ({ ...p, deepLinkUrl: e.target.value }))}
                  placeholder="https://..."
                  sx={{ flex: 1 }}
                  InputProps={{ startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.disabled', fontSize: 20 }} /> }}
                />
              )}
            </Box>
            {form.deepLinkType !== 'NONE' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Tapping the notification will open the {form.deepLinkType === 'SESSION' ? 'session' : form.deepLinkType === 'EVENT' ? 'event' : form.deepLinkType === 'STUDIO' ? 'studio' : ''} page directly in the app.
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
            <Button variant="outlined" onClick={() => setStep(0)} sx={{ borderRadius: 2 }}>← Back</Button>
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={() => setStep(2)}
              disabled={!form.notificationTitle.trim() || !form.notificationBody.trim()}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Preview
            </Button>
          </Box>
        </Box>
      )}

      {/* Step 2: Preview & Send */}
      {step === 2 && (
        <Box>
          <Grid container spacing={4} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <Box sx={{ width: 4, height: 20, bgcolor: '#2196f3', borderRadius: 2 }} />
                <Typography variant="subtitle1" fontWeight={700}>Campaign Summary</Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { label: '📋 Name', value: form.title || '(not set)' },
                  { label: '👥 Audience', value: form._leadUserName ? `${form._leadUserName} (lead)` : SEGMENTS.find((s) => s.value === form.segment)?.label || form.segment },
                  { label: '📣 Title', value: form.notificationTitle },
                  { label: '💬 Body', value: form.notificationBody },
                  ...(form.deepLinkType !== 'NONE' ? [{ label: '🔗 Deep Link', value: `${form.deepLinkType}: ${form.deepLinkId || form.deepLinkUrl || '(not set)'}` }] : []),
                  ...(form.scheduledAt ? [{ label: '⏰ Sends at', value: formatLocalSchedule(form.scheduledAt) || form.scheduledAt }] : []),
                ].map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', gap: 1.5, p: 1.5, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ minWidth: 100, color: 'text.secondary', flexShrink: 0 }}>{item.label}</Typography>
                    <Typography variant="caption" fontWeight={600} sx={{ wordBreak: 'break-all', color: 'text.primary' }}>{item.value}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Schedule picker */}
              <Box sx={{ mt: 2.5, p: 2, bgcolor: 'rgba(255,152,0,0.05)', borderRadius: 2, border: '1px solid rgba(255,152,0,0.2)' }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: '#e65100', display: 'block', mb: 1.5 }}>
                  ⏰ Send Time
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant={!form.scheduledAt ? 'contained' : 'outlined'}
                    onClick={() => setForm((p) => ({ ...p, scheduledAt: '' }))}
                    sx={{ borderRadius: 2, fontSize: '0.8rem', fontWeight: 700 }}
                  >
                    🚀 Send Now
                  </Button>
                  <Button
                    size="small"
                    variant={form.scheduledAt ? 'contained' : 'outlined'}
                    onClick={() => {
                      // Get current IST time + 1h, rounded to next whole hour
                      const istNow = new Date(Date.now() + 5.5 * 3600000);
                      istNow.setUTCMinutes(0, 0, 0);
                      istNow.setUTCHours(istNow.getUTCHours() + 1);
                      setForm((p) => ({ ...p, scheduledAt: istNow.toISOString().slice(0, 16) }));
                    }}
                    sx={{ borderRadius: 2, fontSize: '0.8rem', fontWeight: 700, bgcolor: form.scheduledAt ? '#e65100' : undefined }}
                  >
                    📅 Schedule
                  </Button>
                  {form.scheduledAt && (
                    <TextField
                      type="datetime-local"
                      size="small"
                      value={form.scheduledAt}
                      onChange={(e) => setForm((p) => ({ ...p, scheduledAt: e.target.value }))}
                      inputProps={{ min: nowISTLocal() }}
                      sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                </Box>
                {form.scheduledAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Campaign will be saved and fire automatically at the scheduled time (IST).
                  </Typography>
                )}
              </Box>

              {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
              {notice && <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>{notice}</Alert>}

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={() => setStep(1)} sx={{ borderRadius: 2 }}>← Edit</Button>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : form.scheduledAt ? <CalendarToday /> : <FlashOn />}
                  onClick={handleSend}
                  disabled={loading}
                  sx={{
                    flex: 1,
                    borderRadius: 2,
                    background: form.scheduledAt
                      ? 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)'
                      : 'linear-gradient(135deg, #1565c0 0%, #2196f3 100%)',
                    fontWeight: 700,
                    py: 1.5,
                    boxShadow: form.scheduledAt
                      ? '0 4px 20px rgba(255,152,0,0.35)'
                      : '0 4px 20px rgba(33,150,243,0.35)',
                    '&:hover': {
                      boxShadow: form.scheduledAt
                        ? '0 6px 28px rgba(255,152,0,0.45)'
                        : '0 6px 28px rgba(33,150,243,0.45)',
                    },
                  }}
                >
                  {loading
                    ? (form.scheduledAt ? 'Scheduling...' : 'Sending...')
                    : form.scheduledAt
                    ? `📅 Schedule for ${formatLocalScheduleTime(form.scheduledAt)}`
                    : '🚀 Send Now'}
                </Button>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <NotificationPreview
                title={form.notificationTitle}
                body={form.notificationBody}
                imageUrl={form.imageUrl}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

// ─── Leads Tab ────────────────────────────────
const LeadsTab: React.FC<{ onNotifyLead: (form: Partial<CampaignForm>) => void }> = ({ onNotifyLead }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [daysBack, setDaysBack] = useState(30);
  const [emailSending, setEmailSending] = useState<string | null>(null); // leadId being emailed
  const [emailSentIds, setEmailSentIds] = useState<Set<string>>(new Set());

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res: any = await apiService.getNotificationLeads(daysBack);
      setLeads(Array.isArray(res) ? res : res.leads || res.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [daysBack]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleNotify = (lead: Lead) => {
    const deepLinkType = lead.sessionId ? 'SESSION' : lead.eventId ? 'EVENT' : lead.studioId ? 'STUDIO' : 'NONE';
    const deepLinkId = lead.sessionId || lead.eventId || lead.studioId || '';
    const contextName = lead.sessionClassName || lead.eventName || lead.studioName || 'your session';
    const dateHint = lead.sessionDate
      ? ` on ${formatISTDate(lead.sessionDate)} at ${formatISTTime(lead.sessionDate)}`
      : lead.eventDate
      ? ` on ${formatISTDate(lead.eventDate)} at ${formatISTTime(lead.eventDate)}`
      : '';

    onNotifyLead({
      title: `Reminder — ${lead.user.name || lead.user.email || 'Lead'}`,
      notificationTitle: "Don't miss out! 🏃",
      notificationBody: `Hey {{name}}, you were so close! Complete your booking for ${contextName}${dateHint}. Spots are limited — secure yours now!`,
      segment: 'SPECIFIC_USERS',
      segmentConfig: { userIds: [lead.user.id] },
      deepLinkType,
      deepLinkId,
      deepLinkUrl: '',
      imageUrl: '',
      _leadUserName: lead.user.name || lead.user.email || 'Unknown',
    });
  };

  const handleSendEmail = async (lead: Lead) => {
    setEmailSending(lead.id);
    try {
      await apiService.sendLeadEmailReminder(lead.id);
      setEmailSentIds((prev) => new Set(prev).add(lead.id));
    } catch (err: any) {
      alert(`Failed to send email: ${err.message}`);
    } finally {
      setEmailSending(null);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Abandoned Checkout Leads</Typography>
          <Typography variant="caption" color="text.secondary">
            Users who started booking a future session/event but didn't complete payment — reminders are actionable
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={daysBack}
          exclusive
          onChange={(_, v) => v && setDaysBack(v)}
          size="small"
          sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.75rem', textTransform: 'none', fontWeight: 600 } }}
        >
          <ToggleButton value={7}>7d</ToggleButton>
          <ToggleButton value={14}>14d</ToggleButton>
          <ToggleButton value={30}>30d</ToggleButton>
          <ToggleButton value={60}>60d</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
          <CircularProgress size={32} />
          <Typography color="text.secondary">Loading leads...</Typography>
        </Box>
      ) : leads.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10, bgcolor: '#fff', borderRadius: 2.5, border: '1px dashed rgba(0,0,0,0.12)' }}>
          <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: 'rgba(255,152,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <ShoppingCartOutlined sx={{ fontSize: 34, color: '#ff9800' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>No abandoned checkouts</Typography>
          <Typography variant="body2" color="text.secondary">
            No upcoming sessions or events with abandoned checkouts in the last {daysBack} days
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {leads.map((lead) => {
            const hasSession = !!lead.sessionId;
            const hasEvent = !!lead.eventId;
            const bookingDate = lead.sessionDate || lead.eventDate;
            const until = timeUntil(bookingDate);
            const displayName = lead.sessionClassName || lead.eventName;
            const price = lead.amount ?? lead.sessionPrice ?? lead.eventPrice;

            return (
              <Box
                key={lead.id}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: '#fff',
                  border: '1px solid rgba(0,0,0,0.06)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'rgba(255,152,0,0.35)',
                    boxShadow: '0 4px 16px rgba(255,152,0,0.08)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* User avatar */}
                  <Avatar
                    src={lead.user.image || undefined}
                    sx={{ width: 42, height: 42, bgcolor: '#ff9800', fontSize: '0.9rem', flexShrink: 0, mt: 0.3 }}
                  >
                    {(lead.user.name || '?')[0]?.toUpperCase()}
                  </Avatar>

                  {/* Main content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* User row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.3 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {lead.user.name || lead.user.email || lead.user.phoneNumber || 'Unknown'}
                      </Typography>
                      {lead.user.email && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {lead.user.email}
                        </Typography>
                      )}
                      {lead.user.phoneNumber && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {lead.user.email ? '·' : ''} {lead.user.phoneNumber}
                        </Typography>
                      )}
                      {lead.user.city && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          · {lead.user.city}
                        </Typography>
                      )}
                      {lead.isFromWebsite && (
                        <Chip label="🌐 Web" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(33,150,243,0.1)', color: '#1565c0', fontWeight: 700 }} />
                      )}
                      {!lead.user.hasToken && (
                        <Chip label="No FCM" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(244,67,54,0.1)', color: '#c62828' }} />
                      )}
                      {lead.user.hasToken && (
                        <Chip label="Has app" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(76,175,80,0.1)', color: '#2e7d32' }} />
                      )}
                      <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                        abandoned {timeAgo(lead.abandonedAt)}
                      </Typography>
                    </Box>

                    {/* Booking info row */}
                    <Box
                      sx={{
                        mt: 1,
                        p: 1.5,
                        bgcolor: hasSession
                          ? 'rgba(33,150,243,0.04)'
                          : hasEvent
                          ? 'rgba(156,39,176,0.04)'
                          : 'rgba(0,0,0,0.02)',
                        borderRadius: 1.5,
                        border: hasSession
                          ? '1px solid rgba(33,150,243,0.12)'
                          : hasEvent
                          ? '1px solid rgba(156,39,176,0.12)'
                          : '1px solid rgba(0,0,0,0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* Type badge */}
                      <Chip
                        label={hasSession ? '💪 Session' : hasEvent ? '🎪 Event' : '🏋️ Studio'}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: hasSession
                            ? 'rgba(33,150,243,0.12)'
                            : hasEvent
                            ? 'rgba(156,39,176,0.12)'
                            : 'rgba(0,0,0,0.06)',
                          color: hasSession ? '#1565c0' : hasEvent ? '#6a1b9a' : 'text.secondary',
                        }}
                      />

                      {/* Name */}
                      {(displayName || lead.studioName) && (
                        <Typography variant="caption" fontWeight={700} noWrap sx={{ color: 'text.primary' }}>
                          {displayName || lead.studioName}
                        </Typography>
                      )}

                      {/* Studio subtext for session/event */}
                      {lead.studioName && displayName && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          @ {lead.studioName}
                        </Typography>
                      )}

                      {/* Date/Time in IST */}
                      {bookingDate && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarToday sx={{ fontSize: 12, color: '#2196f3' }} />
                          <Typography variant="caption" fontWeight={600} sx={{ color: '#1565c0', whiteSpace: 'nowrap' }}>
                            {formatISTDate(bookingDate)} · {formatISTTime(bookingDate)} IST
                          </Typography>
                          {until && (
                            <Chip
                              label={until}
                              size="small"
                              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'rgba(76,175,80,0.12)', color: '#2e7d32', ml: 0.5 }}
                            />
                          )}
                        </Box>
                      )}

                      {/* Duration for sessions */}
                      {lead.sessionDuration && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {lead.sessionDuration} min
                        </Typography>
                      )}

                      {/* Price */}
                      {price != null && price > 0 && (
                        <Chip
                          label={`₹${price}`}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(76,175,80,0.1)', color: '#2e7d32' }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flexShrink: 0, mt: 0.3 }}>
                    {/* Push notification button — only if user has FCM token */}
                    <Tooltip title={lead.user.hasToken ? 'Send push notification with deep link' : 'No FCM token — user not on app'}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<ArrowForwardIos sx={{ fontSize: 11 }} />}
                          onClick={() => handleNotify(lead)}
                          disabled={!lead.user.hasToken}
                          sx={{
                            borderRadius: 2,
                            fontSize: '0.72rem',
                            py: 0.4,
                            px: 1.5,
                            borderColor: 'rgba(255,152,0,0.4)',
                            color: '#e65100',
                            '&:hover': { bgcolor: 'rgba(255,152,0,0.08)', borderColor: '#ff9800' },
                            whiteSpace: 'nowrap',
                            width: '100%',
                          }}
                        >
                          Push Remind
                        </Button>
                      </span>
                    </Tooltip>

                    {/* Email button — show if user has email */}
                    {lead.user.email && (
                      <Tooltip title={emailSentIds.has(lead.id) ? 'Email sent!' : `Send reminder email to ${lead.user.email}`}>
                        <span>
                          <Button
                            size="small"
                            variant={emailSentIds.has(lead.id) ? 'contained' : 'outlined'}
                            onClick={() => !emailSentIds.has(lead.id) && handleSendEmail(lead)}
                            disabled={emailSending === lead.id || emailSentIds.has(lead.id)}
                            startIcon={emailSending === lead.id ? <CircularProgress size={12} /> : undefined}
                            sx={{
                              borderRadius: 2,
                              fontSize: '0.72rem',
                              py: 0.4,
                              px: 1.5,
                              whiteSpace: 'nowrap',
                              width: '100%',
                              ...(emailSentIds.has(lead.id)
                                ? { bgcolor: '#2e7d32', color: '#fff', '&:hover': { bgcolor: '#2e7d32' } }
                                : { borderColor: 'rgba(33,150,243,0.4)', color: '#1565c0', '&:hover': { bgcolor: 'rgba(33,150,243,0.06)', borderColor: '#2196f3' } }
                              ),
                            }}
                          >
                            {emailSentIds.has(lead.id) ? '✓ Email Sent' : 'Send Email'}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ─── Main Page ────────────────────────────────
const PushCenter: React.FC = () => {
  const { admin } = useAuth();
  const isStudio = apiService.isStudio();
  const [tab, setTab] = useState(0);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<Campaign | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  // Pre-fill from Leads tab (admin only)
  const [pendingLeadForm, setPendingLeadForm] = useState<Partial<CampaignForm> | undefined>(undefined);

  const fetchCampaigns = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const res: any = isStudio
        ? await apiService.getStudioNotificationCampaigns()
        : await apiService.getNotificationCampaigns();
      setCampaigns(res.campaigns || res.data || []);
    } catch (err: any) {
      if (!silent) setError(err.message || 'Failed to load campaigns');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isStudio]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  // A campaign send returns whatever the create call captured — which, on a slow
  // send or a client timeout, is a not-yet-settled snapshot. Pull the list now
  // and again a few times so the card catches up to the real SENT status/counts.
  const pollRef = useRef<number | null>(null);
  const stopCampaignPoll = useCallback(() => {
    if (pollRef.current !== null) { window.clearInterval(pollRef.current); pollRef.current = null; }
  }, []);
  useEffect(() => stopCampaignPoll, [stopCampaignPoll]);

  const refreshCampaignsSoon = useCallback(() => {
    fetchCampaigns();
    stopCampaignPoll();
    let n = 0;
    pollRef.current = window.setInterval(() => {
      n += 1;
      fetchCampaigns(true);
      if (n >= 5) stopCampaignPoll();
    }, 8000);
  }, [fetchCampaigns, stopCampaignPoll]);

  const handleCampaignSuccess = (campaign: Campaign) => {
    setCampaigns((prev) => [campaign, ...prev]);
    setTab(1);
    const msg = campaign.status === 'SCHEDULED'
      ? `"${campaign.title}" scheduled for ${formatScheduledAt(campaign.scheduledAt)}.`
      : `"${campaign.title}" sent! ${campaign.successCount} delivered.`;
    setSnackbar({ open: true, message: msg, severity: 'success' });
    if (campaign.status !== 'SCHEDULED') refreshCampaignsSoon();
  };

  const handleCancelSchedule = async (campaign: Campaign) => {
    try {
      await apiService.cancelScheduledCampaign(campaign._id);
      setCampaigns((prev) => prev.map((c) =>
        c._id === campaign._id ? { ...c, status: 'DRAFT', scheduledAt: undefined } : c
      ));
      setSnackbar({ open: true, message: `"${campaign.title}" schedule cancelled.`, severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to cancel schedule', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      setDeleteLoading(true);
      if (isStudio) {
        // Pinned to the campaign's own location (see createStudioNotificationCampaign).
        await apiService.deleteStudioNotificationCampaign(deleteDialog._id, deleteDialog.studioId);
      } else {
        await apiService.deleteNotificationCampaign(deleteDialog._id);
      }
      setCampaigns((prev) => prev.filter((c) => c._id !== deleteDialog._id));
      setDeleteDialog(null);
      setSnackbar({ open: true, message: 'Campaign deleted', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Delete failed', severity: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLeadNotify = (form: Partial<CampaignForm>) => {
    setPendingLeadForm(form);
    setTab(0); // switch to Create tab
  };

  // Stats
  const sentCampaigns = campaigns.filter((c) => c.status === 'SENT');
  const totalDelivered = campaigns.reduce((s, c) => s + (c.successCount || 0), 0);
  const totalOpened = campaigns.reduce((s, c) => s + (c.openedCount || 0), 0);
  const totalTargeted = campaigns.reduce((s, c) => s + (c.targetedCount || 0), 0);
  const avgRate = totalTargeted > 0 ? Math.round((totalDelivered / totalTargeted) * 100) : 0;

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
          borderRadius: 3,
          p: 3,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            right: -40,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'rgba(33,150,243,0.08)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 50, height: 50, borderRadius: 2.5, bgcolor: 'rgba(33,150,243,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <NotificationsActive sx={{ color: '#64b5f6', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, letterSpacing: '-0.3px' }}>
              Notification Center
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              {isStudio
                ? 'Send push notifications to your studio\'s users · track delivery'
                : 'Send targeted push notifications · track delivery · deep links · manage leads'}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setTab(0)}
          sx={{
            background: 'linear-gradient(135deg, #1565c0 0%, #2196f3 100%)',
            borderRadius: 2,
            fontWeight: 700,
            display: { xs: 'none', sm: 'flex' },
            boxShadow: '0 4px 16px rgba(33,150,243,0.35)',
          }}
        >
          New Campaign
        </Button>
      </Box>

      {/* Stats */}
      {!loading && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 3 }}>
            <StatCard
              label="Campaigns Sent"
              value={sentCampaigns.length}
              icon={<Send sx={{ color: '#fff', fontSize: 22 }} />}
              gradient="linear-gradient(135deg, #1565c0 0%, #2196f3 100%)"
              iconBg="rgba(255,255,255,0.15)"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <StatCard
              label="Total Delivered"
              value={totalDelivered.toLocaleString()}
              icon={<People sx={{ color: '#fff', fontSize: 22 }} />}
              gradient="linear-gradient(135deg, #1b5e20 0%, #4caf50 100%)"
              iconBg="rgba(255,255,255,0.15)"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <StatCard
              label="Total Opened"
              value={totalOpened.toLocaleString()}
              icon={<TouchApp sx={{ color: '#fff', fontSize: 22 }} />}
              gradient="linear-gradient(135deg, #4a148c 0%, #9c27b0 100%)"
              iconBg="rgba(255,255,255,0.15)"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <StatCard
              label="Avg. Delivery Rate"
              value={`${avgRate}%`}
              icon={<TrendingUp sx={{ color: '#fff', fontSize: 22 }} />}
              gradient={avgRate >= 80
                ? 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)'
                : 'linear-gradient(135deg, #880e4f 0%, #e91e63 100%)'}
              iconBg="rgba(255,255,255,0.15)"
            />
          </Grid>
        </Grid>
      )}

      {/* Main Card */}
      <Paper elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.07)', borderRadius: 3, overflow: 'hidden' }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.07)', bgcolor: '#fff', px: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem', minHeight: 52 },
              '& .Mui-selected': { color: '#2196f3' },
              '& .MuiTabs-indicator': { bgcolor: '#2196f3', height: 3, borderRadius: '3px 3px 0 0' },
            }}
          >
            <Tab
              icon={<Send sx={{ fontSize: 17 }} />}
              iconPosition="start"
              label="Create Campaign"
            />
            <Tab
              icon={
                <Badge badgeContent={campaigns.length || null} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}>
                  <BarChart sx={{ fontSize: 17 }} />
                </Badge>
              }
              iconPosition="start"
              label="History"
            />
            {!isStudio && (
              <Tab
                icon={<ShoppingCartOutlined sx={{ fontSize: 17 }} />}
                iconPosition="start"
                label="Leads"
                sx={{ '&.Mui-selected': { color: '#e65100' }, '& .MuiTab-iconWrapper': { color: 'inherit' } }}
              />
            )}
          </Tabs>
        </Box>

        <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
          {/* Tab 0: Create */}
          {tab === 0 && (
            <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: 2.5, border: '1px solid rgba(0,0,0,0.06)' }}>
              <CreateCampaignForm
                onSuccess={handleCampaignSuccess}
                onRefresh={refreshCampaignsSoon}
                adminEmail={admin?.email || admin?.name || 'admin'}
                initialForm={pendingLeadForm}
                initialStep={pendingLeadForm ? 1 : 0}
                onFormConsumed={() => setPendingLeadForm(undefined)}
                isStudio={isStudio}
              />
            </Box>
          )}

          {/* Tab 1: History */}
          {tab === 1 && (
            <Box>
              {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

              {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                  <CircularProgress size={36} />
                  <Typography variant="body2" color="text.secondary">Loading campaigns...</Typography>
                </Box>
              ) : campaigns.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10, bgcolor: '#fff', borderRadius: 2.5, border: '1px dashed rgba(0,0,0,0.12)' }}>
                  <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: 'rgba(33,150,243,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <NotificationsIcon sx={{ fontSize: 34, color: '#2196f3' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom>No campaigns yet</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Send your first notification campaign to see results here
                  </Typography>
                  <Button variant="contained" startIcon={<Add />} onClick={() => setTab(0)} sx={{ borderRadius: 2 }}>
                    Create First Campaign
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {campaigns.map((campaign) => (
                    <CampaignCard
                      key={campaign._id}
                      campaign={campaign}
                      onView={() => setSelectedCampaign(campaign)}
                      onDelete={() => setDeleteDialog(campaign)}
                      onCancelSchedule={campaign.status === 'SCHEDULED' ? () => handleCancelSchedule(campaign) : undefined}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Tab 2: Leads */}
          {tab === 2 && (
            <LeadsTab onNotifyLead={handleLeadNotify} />
          )}
        </Box>
      </Paper>

      {/* Campaign Detail Dialog */}
      <CampaignDetailDialog campaign={selectedCampaign} onClose={() => setSelectedCampaign(null)} />

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(244,67,54,0.1)', width: 40, height: 40 }}>
              <Delete sx={{ color: '#f44336', fontSize: 20 }} />
            </Avatar>
            <Typography fontWeight={700}>Delete Campaign?</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently delete <strong>"{deleteDialog?.title}"</strong>. Campaign history cannot be recovered.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteDialog(null)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={14} color="inherit" /> : <Delete />}
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity} variant="filled" sx={{ minWidth: 300, borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/**
 * ENGAGEMENT — push notifications and WhatsApp in one place.
 *
 * Push is available as before. WhatsApp appears only for SweatSocial super
 * admins and for a studio's owner/manager once messaging is enabled for that
 * studio (the backend enforces the same rule).
 */
const NotificationsPage: React.FC = () => {
  const { admin } = useAuth();
  const [params, setParams] = useSearchParams();
  const showWhatsApp = canUseWhatsApp(admin);
  const whatsAppLocked = isWhatsAppLocked(admin);
  const pushLocked = isPushLocked(admin);
  const studioName = (admin?.studio as any)?.studioName;
  const showToggle = showWhatsApp || whatsAppLocked || pushLocked;
  // No app but WhatsApp on: open on the channel they can actually use.
  const requested = params.get('channel') ?? (pushLocked && showWhatsApp ? 'whatsapp' : 'push');
  const channel = showToggle && requested === 'whatsapp' ? 'whatsapp' : 'push';

  const setChannel = (c: 'push' | 'whatsapp') => {
    const next = new URLSearchParams(params);
    next.set('channel', c);
    setParams(next, { replace: true });
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mb: 2.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.3px' }}>Engagement</Typography>
          <Typography variant="body2" color="text.secondary">
            Reach your members with push notifications{showWhatsApp ? ' and WhatsApp' : ''}.
          </Typography>
        </Box>
        {showToggle && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={channel}
            onChange={(_, v) => v && setChannel(v)}
            sx={{
              // Soft segmented control: a warm track, the open channel sits on a white card.
              bgcolor: '#F4F1EE',
              borderRadius: 999,
              p: 0.5,
              gap: 0.5,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                px: 4.5,
                py: 0.75,
                gap: 0.75,
                border: 'none !important',
                borderRadius: '999px !important',
                color: '#6B6560',
                transition: 'background-color .15s, box-shadow .15s, color .15s',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              },
              '& .MuiToggleButton-root.Mui-selected': {
                bgcolor: '#fff',
                color: '#2B2724',
                boxShadow: '0 1px 3px rgba(43,39,36,0.12)',
                '&:hover': { bgcolor: '#fff' },
              },
              // WhatsApp is a selling point: always green, and a faint green ring when open.
              '& .MuiToggleButton-root[value="whatsapp"]': { color: '#128C7E' },
              '& .MuiToggleButton-root.Mui-selected[value="whatsapp"]': {
                color: '#075E54',
                boxShadow: '0 1px 3px rgba(43,39,36,0.12), inset 0 0 0 1px rgba(37,211,102,0.45)',
              },
            }}
          >
            <ToggleButton value="push">
              <NotificationsActive sx={{ fontSize: 18 }} /> Push notifications
              {pushLocked && <LockOutlined sx={{ fontSize: 13, color: '#A39C95' }} />}
            </ToggleButton>
            <ToggleButton value="whatsapp">
              <WhatsApp sx={{ fontSize: 18, color: '#25D366' }} /> WhatsApp
              {whatsAppLocked && <LockOutlined sx={{ fontSize: 13, color: '#A39C95' }} />}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>
      {channel === 'whatsapp'
        ? (showWhatsApp ? <WhatsAppHub /> : <WhatsAppLocked studioName={studioName} />)
        : pushLocked
          ? <FeatureLocked reason="engagement" studioName={studioName} />
          : <PushCenter />}
    </Box>
  );
};

export default NotificationsPage;
